import {
  useState,
  useCallback,
  createContext,
  ReactNode,
  useEffect,
} from 'react';

import { Permission } from 'enums';
import {
  BadRequestError,
  RoleNotFoundError,
  TokenNotFoundError,
  UserNotFoundError,
} from 'errors';
import { useQuery } from 'hooks';
import { User } from 'models';
import { baseService } from 'services';
import {
  safeStorage,
  getLoggedUser,
  getUserToken,
  updateGlobalPreLoading,
} from 'utils';

export interface AuthContextData {
  user: User | undefined;
  token: string | null;
  updateToken(newToken: string): void;
  hasPermissions(permissions: Permission[]): boolean;
}

export const AuthContext = createContext<AuthContextData>(
  {} as AuthContextData,
);

interface AuthProviderProps {
  children: ReactNode;
}

enum StorageKeys {
  USER = 'user',
  TOKEN = 'token',
  ENTITY = 'entity',
  USER_PERMISSIONS = 'user-permissions',
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const query = useQuery();
  const queryToken = query.get('token');

  const [error, setError] = useState<any | undefined>();
  const [token, setToken] = useState<string | null>(() => {
    if (queryToken) {
      safeStorage.setItem(StorageKeys.TOKEN, queryToken);
      baseService.defaults.headers.Authorization = `Bearer ${queryToken}`;
      return queryToken;
    }

    const storagedToken = safeStorage.getItem(StorageKeys.TOKEN);

    if (storagedToken) {
      baseService.defaults.headers.Authorization = `Bearer ${storagedToken}`;
      return storagedToken;
    }

    const token = getUserToken();

    if (token) {
      return token;
    }

    setError(new TokenNotFoundError());

    return null;
  });

  const [user] = useState<User | undefined>(() => {
    const loggedUser = getLoggedUser();

    if (!loggedUser) {
      setError(new UserNotFoundError());
      return undefined;
    }

    return loggedUser;
  });

  const configureUserSession = useCallback((loggedUser: User) => {
    if (loggedUser && loggedUser.role) {
      baseService.defaults.headers['x-role'] = loggedUser.role;
      return;
    }
    setError(new RoleNotFoundError());
  }, []);

  const hasPermissions = useCallback(
    (permissions: Permission[]) => {
      const decoratedPermissions = [...permissions, Permission.DEVOLUTIVE];
      const userHasAnyPermissions = user?.permissions.filter(permission =>
        decoratedPermissions.includes(permission),
      );

      return !!userHasAnyPermissions?.length;
    },
    [user],
  );

  const updateToken = useCallback((newToken: string) => {
    safeStorage.setItem(StorageKeys.TOKEN, newToken);
    baseService.defaults.headers.Authorization = `Bearer ${newToken}`;
    setToken(newToken);
  }, []);

  if (error) {
    updateGlobalPreLoading(false);
    throw error;
  }

  useEffect(() => {
    if (user) configureUserSession(user);
  }, [configureUserSession, user]);

  useEffect(() => {
    const entityType = query.get('entityType');
    const indicatorEventId = query.get('indicatorEventId');
    const entityId = query.get('entityId');

    if (!(entityType || entityId || indicatorEventId)) {
      setError(() => new BadRequestError());
    }
  }, [query]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        hasPermissions,
        updateToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
